import React, { useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Paperclip } from "lucide-react";

export function ClaimForm({ onSubmitted }) {
  const [form, setForm] = useState({
    claim_type: "Reimbursement",
    hospital: "",
    treatment: "",
    amount: "",
    admission_date: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [ocrAmount, setOcrAmount] = useState(0);
  const [billFileId, setBillFileId] = useState(null);
  const [scanning, setScanning] = useState(false);
  const fileRef = useRef(null);

  const scanBill = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/claims/ocr", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({
        ...f,
        amount: data.amount ? String(data.amount) : f.amount,
        hospital: data.hospital || f.hospital,
        admission_date: f.admission_date,
      }));
      setOcrAmount(data.amount || 0);
      setBillFileId(data.bill_file_id || null);
      toast.success(data.amount ? `Read ₹${Number(data.amount).toLocaleString("en-IN")} from your bill` : "Bill scanned — please enter the amount");
    } catch (err) {
      toast.error("Couldn't read the bill. Please enter details manually.");
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.hospital || !form.treatment || !form.amount) {
      toast.error("Please fill hospital, treatment and amount.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/claims", {
        ...form,
        amount: parseFloat(form.amount),
        ocr_amount: ocrAmount,
        bill_file_id: billFileId,
      });
      toast.success(`Claim ${data.claim_number} submitted!`);
      setDone(true);
      onSubmitted?.(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="claim-form-done" className="w-full max-w-sm rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-800">
        Your claim has been submitted successfully. See the details below.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="claim-form"
      className="w-full max-w-sm rounded-xl border border-zinc-200 bg-white p-5 shadow-sm space-y-3"
    >
      <p className="font-heading font-semibold text-emerald-950">File a new claim</p>

      <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50/60 p-3">
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf"
          className="hidden"
          data-testid="claim-bill-input"
          onChange={scanBill}
        />
        <button
          type="button"
          data-testid="claim-scan-bill"
          disabled={scanning}
          onClick={() => fileRef.current?.click()}
          className="w-full flex items-center justify-center gap-2 rounded-full bg-white border border-emerald-300 text-emerald-800 text-sm font-medium px-4 py-2 hover:bg-emerald-100 transition-colors disabled:opacity-60"
        >
          <Paperclip size={16} />
          {scanning ? "Reading bill…" : "Scan bill photo (auto-fill amount)"}
        </button>
        {ocrAmount > 0 && (
          <p data-testid="claim-ocr-hint" className="mt-2 text-[11px] text-emerald-700">
            Read ₹{Number(ocrAmount).toLocaleString("en-IN")} from your bill. Edit the amount below if it looks wrong — we'll flag large differences for review.
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Claim type</Label>
        <Select value={form.claim_type} onValueChange={(v) => setForm({ ...form, claim_type: v })}>
          <SelectTrigger data-testid="claim-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Cashless">Cashless</SelectItem>
            <SelectItem value="Reimbursement">Reimbursement</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Hospital</Label>
        <Input data-testid="claim-hospital" value={form.hospital}
          onChange={(e) => setForm({ ...form, hospital: e.target.value })}
          placeholder="e.g. Apollo Hospital, Bengaluru" />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">Treatment / reason</Label>
        <Input data-testid="claim-treatment" value={form.treatment}
          onChange={(e) => setForm({ ...form, treatment: e.target.value })}
          placeholder="e.g. Appendectomy" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Amount (₹)</Label>
          <Input data-testid="claim-amount" type="number" value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })}
            placeholder="50000" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Admission date</Label>
          <Input data-testid="claim-date" type="date" value={form.admission_date}
            onChange={(e) => setForm({ ...form, admission_date: e.target.value })} />
        </div>
      </div>

      <Button type="submit" disabled={loading} data-testid="claim-submit"
        className="w-full rounded-full bg-emerald-900 hover:bg-emerald-800 text-white transition-colors">
        {loading ? "Submitting…" : "Submit Claim"}
      </Button>
    </form>
  );
}
